<template>
	<label :for="`multiselect-${name}`" :class="[{ 'has-error': !!error }]">
		<span class="label" :class="labelClassesToApply" v-if="!!label && !$slots.label">{{
			label
		}}</span>
		<slot name="label" />
		<div
			class="multiselect-wrapper"
			:class="[{ open }, { emptySearch }]"
			v-focus-outside="closeOptions"
			v-click-outside="closeOptions"
		>
			<div
				tabindex="0"
				:id="`multiselect-${name}`"
				class="bbox-multiselect"
				:class="[
					{
						[`${populatedContainerClasses}`]: !empty,
						[`${containerClasses}`]: !!empty,
						[`${wrapperErrorClasses}`]: error,
						chevron: true,
						'chevron-down': openDirection === 'down',
						'chevron-up': openDirection !== 'down'
					}
				]"
				ref="multiselect"
				@keydown.down="next($event)"
				@keydown.up="previous($event)"
				@keydown.esc="
					closeOptions();
					$refs.multiselect.focus();
				"
				@keydown.enter="open ? selectCurrent() : openOptions()"
				@focus="focusOnInput()"
				@mousedown="focusOnInput()"
				@click="toggleOptions($event)"
			>
				{{ !searchable && empty ? placeholder : null }}
				<div
					v-for="(option, index) in selectedOptions"
					:class="[selectedOptionClasses]"
					:key="index"
					@click.stop="removeOption(option)"
				>
					{{ typeof option === 'object' ? option.name : option }}
					<div :class="[closeButtonClasses]">×</div>
				</div>
				<input
					v-if="searchable"
					:class="[[inputClasses], { empty: empty }]"
					type="text"
					:value="search"
					ref="input"
					@input="searchOptions($event.target.value)"
					@keydown.esc="
						closeOptions();
						focusOnInput();
					"
					@keydown.backspace="$event.target.value === '' ? selectedOptions.pop() : null"
					@focusout="removeHovering()"
					:placeholder="inputPlaceholder"
				/>
			</div>
			<ul
				:id="`multiselect-options-${name}`"
				class="options border border-white-darker-3"
				:style="{ top: optionsTop }"
				ref="options"
			>
				<div class="wrapper">
					<li
						v-for="(option, index) in showOptions"
						:class="[
							{
								selected: selectedOptions.includes(option),
								[`${optionHoverClasses}`]: index === currentHover,
								[`${optionClasses}`]: index !== currentHover
							}
						]"
						:key="index"
						@click="select(option, true)"
						:ref="'option-' + index"
						@keyup.enter="select(option, false)"
						@keyup.esc="
							closeOptions();
							$event.target.blur();
						"
						@focus="$event.target.classList.remove('hovering')"
					>
						{{ typeof option === 'object' ? option.name : option }}
						<span
							v-if="
								typeof option === 'object'
									? selectedOptions.some(e => e.value == option.value)
									: selectedOptions.includes(option)
							"
							style="font-size: 16px"
							class="icon-right"
							><b-icon><icon-done /></b-icon
						></span>
					</li>
				</div>
			</ul>
		</div>
		<span class="input-error font-size-big red-base margin-top-tiny" v-if="!!error"
			><b-icon><icon-error /></b-icon> {{ error }}</span
		>
	</label>
</template>

<script>
import BIcon from './Icon.vue';

export default {
	name: 'b-multi-select',
	components: {
		BIcon
	},
	props: {
		/**
		 * Classes para estilizar o label
		 */
		labelClasses: {
			type: String,
			default: 'display-block margin-bottom-tiny ink-base'
		},
		/**
		 * Classes para estilizar o container do multiselect
		 */
		containerClasses: {
			type: String,
			default:
				'ink-base border border-white-darker-3 bg-white-darker-2 padding-normal padding font-size-big border-focus-ink-lighter-2 display-flex flex-wrap'
		},
		/**
		 * Classes para estilizar o container do multiselect quando há opções selecionadas
		 */
		populatedContainerClasses: {
			type: String,
			default:
				'ink-base border border-white-darker-3 bg-white-darker-2 padding-top-tiny padding-left-normal padding-0 font-size-big border-focus-ink-lighter-2 display-flex flex-wrap'
		},
		/**
		 * Classes para estilizar o input do multiselect
		 */
		inputClasses: {
			type: String,
			default:
				'ink-base display-flex border-none border bg-transparent padding-0 font-size-big margin-bottom-tiny padding-top-tiny padding-bottom-tiny'
		},
		/**
		 * Classes para estilizar as opções do multiselect
		 */
		optionClasses: {
			type: String,
			default:
				'border-top border-bottom border-white-darker-3 padding-normal font-size-big bg-white-base bg-hover-blue-lighter-4'
		},
		/**
		 * Classes para estilizar o hover das opções do multiselect
		 */
		optionHoverClasses: {
			type: String,
			default:
				'border-top border-bottom border-white-darker-3 padding-normal font-size-big bg-blue-lighter-4'
		},
		/**
		 * Classes para estilizar as opções selecionadas do multiselect
		 */
		selectedOptionClasses: {
			type: String,
			default:
				'border border-radius border-white-darker-3 padding-tiny padding-right-0 font-size-big bg-white-base bg-hover-blue-lighter-4 margin-0 margin-bottom-tiny margin-right-tiny display-flex'
		},
		/**
		 * Classes para estilizar o hover das opções selecionadas do multiselect
		 */
		closeButtonClasses: {
			type: String,
			default: 'padding-tiny padding-top-0 padding-bottom-0 ink-lighter-1'
		},
		/**
		 * Classes para estilizar o label quando há uma mensagem de erro
		 */
		labelErrorClasses: {
			type: String,
			default: 'display-block margin-bottom-tiny ink-base'
		},
		/**
		 * Classes para estilizar o multiselect quando há uma mensagem de erro
		 */
		wrapperErrorClasses: {
			type: String,
			default:
				'ink-base border border-red-lighter-2 border-focus-red-base  bg-red-lighter-4 bg-focus-red-lighter-3 font-size-big '
		},
		/**
		 * Valores das opções do multiselect, pode ser tanto um array de Strings, como um array de objetos, ex: `[{name:'nome', value:'valor'}]`
		 */
		options: Array,
		/**
		 * Name padrão do multiselect
		 */
		name: {
			type: String,
			required: true
		},
		/**
		 * Valor exibido como placeholder do multiselect
		 */
		placeholder: {
			type: String,
			default: 'Selecione...',
			label: String
		},
		/**
		 * Valor exibido como placeholder do multiselect com input
		 */
		inputPlaceholder: {
			type: String,
			default: 'Digite ou selecione...',
			label: String
		},
		/**
		 * Texto do label do multiselect
		 */
		label: String,
		/**
		 * Mensagem de erro
		 */
		error: {
			type: String,
			default: ''
		},
		/**
		 * Valor selecionado no select
		 * @model
		 */
		value: {
			default: Array
		},
		/**
		 * Se true as opções do multiselect podem ser filtradas
		 */
		searchable: Boolean
	},
	directives: {
		'click-outside': {
			bind: function(el, binding, vnode) {
				el.clickOutsideEvent = function(event) {
					// here I check that click was outside the el and his childrens
					if (!(el == event.target || el.contains(event.target))) {
						// and if it did, call method provided in attribute value
						vnode.context[binding.expression](event);
					}
				};
				document.body.addEventListener('click', el.clickOutsideEvent);
			},
			unbind: function(el) {
				document.body.removeEventListener('click', el.clickOutsideEvent);
			}
		},
		'focus-outside': {
			bind: function(el, binding, vnode) {
				el.focusOutsideEvent = function(event) {
					// here I check that focus was outside the el and his childrens
					if (!(el == event.target || el.contains(event.target))) {
						// and if it did, call method provided in attribute value
						vnode.context[binding.expression](event);
					}
				};
				document.addEventListener('focusin', el.focusOutsideEvent);
			},
			unbind: function(el) {
				document.removeEventListener('focusin', el.focusOutsideEvent);
			}
		}
	},
	data() {
		return {
			showOptions: this.options,
			selectedOptions: this.value,
			currentHover: null,
			open: false,
			search: '',
			optionsTop: '',
			openDirection: 'down'
		};
	},
	computed: {
		empty() {
			return this.selectedOptions.length ? false : true;
		},
		emptySearch() {
			return this.search === '' && !this.selectedOptions.length ? true : false;
		},
		labelClassesToApply() {
			if (this.error) return this.labelErrorClasses;
			else return this.labelClasses;
		}
	},
	updated() {
		const firstListItem = this.$refs.options.querySelectorAll('li')[0];
		if (this.open && this.showOptions.length && this.search) {
			firstListItem.classList.add('hovering');
			this.currenHover = 0;
		} else if (this.search == '' && this.open) {
			firstListItem.classList.remove('hovering');
		}
	},
	methods: {
		searchOptions(val) {
			if (!this.open) this.open = true;
			if (val === '') {
				this.search = val;
				this.showOptions = this.options;
			} else {
				let original = val;
				val = val.toString().toLowerCase();
				this.showOptions = this.options.filter(option => {
					if (typeof option === 'object')
						return (
							option.name
								.toString()
								.toLowerCase()
								.includes(val) ||
							option.value
								.toString()
								.toLowerCase()
								.includes(val)
						);
					return option
						.toString()
						.toLowerCase()
						.includes(val);
				});

				this.updateListLocation();
				this.search = original;
				this.currentHover = 0;
			}
		},
		selectCurrent() {
			this.select(this.showOptions[this.currentHover]);
			this.open = true;
		},
		closeOptions() {
			this.open = false;
		},
		focusOnInput() {
			if (this.searchable) this.$refs.input.focus();
			else this.$refs.multiselect.focus();
			this.search = '';
		},
		openOptions() {
			this.focusOnInput();
			this.showOptions = this.options;
			this.open = true;
			this.updateListLocation();
		},
		updateListLocation() {
			let _this = this;
			setTimeout(function() {
				const options = _this.$refs.options;

				const inputOrSelect = _this.searchable ? _this.$refs.input : _this.$refs.multiselect;

				let intViewportHeight = window.innerHeight;
				let inputOrSelectOffsetBottom = inputOrSelect.getBoundingClientRect().bottom;
				let optionsOffsetTop = options.getBoundingClientRect().top;
				let optionsOffsetBottom = options.getBoundingClientRect().bottom;
				let optionsHeight = optionsOffsetBottom - optionsOffsetTop;
				let availableSpace = intViewportHeight - inputOrSelectOffsetBottom;

				if (availableSpace > optionsHeight) {
					_this.optionsTop = 'calc(100% + 8px)';
					_this.openDirection = 'down';
				} else {
					_this.optionsTop = `calc(-${optionsHeight}px - 8px)`;
					_this.openDirection = 'up';
				}
			}, 10);
		},
		toggleOptions(event) {
			if (this.open) {
				this.closeOptions();
			} else if (Object.keys(this.options).length) {
				this.openOptions(event);
			}
		},
		selectIcon() {
			const inputOrSelect = this.searchable ? this.$refs.input : this.$refs.multiselect;

			let intViewportHeight = window.innerHeight;
			let inputOrSelectOffsetTop = inputOrSelect.getBoundingClientRect().top;
			let inputOrSelectOffsetBottom = inputOrSelect.getBoundingClientRect().bottom;

			if (
				intViewportHeight - inputOrSelectOffsetBottom >
				inputOrSelectOffsetBottom - inputOrSelectOffsetTop
			)
				this.openDirection = 'down';
			else this.openDirection = 'up';
		},
		select(option, itShouldSetCurrentHoverToNull = false) {
			this.showOptions = this.options;

			if(itShouldSetCurrentHoverToNull === true) this.currentHover = null

			/**
			 * Evento emitido quando usuário troca o valor selecionado do select
			 * @property {option}
			 * @type {change}
			 */
			this.$emit('change', this.selectedOptions);
			/**
			 * Evento emitido quando usuário troca o valor selecionado do select
			 * @property {option}
			 * @type {input}
			 */
			this.$emit('input', this.selectedOptions);

			if (typeof option === 'object') {
				if (!this.selectedOptions.some(e => e.value === option.value)) {
					if (this.showOptions.some(e => e.value === option.value)) {
						this.selectedOptions.push(option);
						this.search = '';
					}
				} else {
					this.removeOption(option);
					return;
				}
			} else {
				if (!this.selectedOptions.includes(option)) {
					if (this.showOptions.includes(option)) {
						this.selectedOptions.push(option);
						this.search = '';
					}
					return;
				} else {
					this.removeOption(option);
					return;
				}
			}
			this.focusOnInput();
		},
		removeOption(option) {
			if (typeof option === 'object') {
				if (this.selectedOptions.some(e => e.value === option.value)) {
					this.selectedOptions = [...this.selectedOptions.filter(e => e.value !== option.value)];
				}
			} else if (this.selectedOptions.includes(option)) {
				this.selectedOptions.splice(this.selectedOptions.indexOf(option), 1);
			}
			/**
			 * Evento emitido quando usuário troca o valor selecionado do select
			 * @property {option}
			 * @type {change}
			 */
			this.$emit('change', this.selectedOptions);
			/**
			 * Evento emitido quando usuário troca o valor selecionado do select
			 * @property {option}
			 * @type {input}
			 */
			this.$emit('input', this.selectedOptions);
			this.focusOnInput();
		},
		removeHovering() {
			let hovering = this.$refs.options.querySelectorAll('li.hovering')[0];
			if (hovering) {
				hovering.classList.remove('hovering');
				hovering.setAttribute('tabindex', '0');
			}
		},
		next(e) {
			e.preventDefault();
			if (this.currentHover + 1 < this.showOptions.length) {
				this.currentHover++;
				this.$refs['option-' + this.currentHover][0].scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'start'
				});
			}
		},
		previous(e) {
			e.preventDefault();
			if (this.currentHover - 1 >= 0) {
				this.currentHover--;
				this.$refs['option-' + this.currentHover][0].scrollIntoView({
					behavior: 'smooth',
					block: 'nearest',
					inline: 'start'
				});
			}
		}
	},
	mounted() {
		const width = this.$refs.options.scrollWidth;
		this.$refs.multiselect.style.minWidth = width;
		this.open = false;
		this.selectIcon();
	}
};
</script>

<style scoped lang="scss">
label,
label * {
	box-sizing: border-box;
}
label {
	display: block;
}
.multiselect-wrapper {
	position: relative;
	width: 100%;
	display: inline-block; //to counter margin merging
	align-items: center;
	line-height: 1;
	&.emptySearch input,
	&.emptySearch .bbox-multiselect {
		color: #8f8a9b;
	}
}
.has-error .multiselect-wrapper,
.has-error .multiselect-wrapper {
	margin-bottom: 0;
}
.has-error .multiselect-wrapper.open {
	margin-bottom: calc(1.18rem + 0.5em);
}
ul.options {
	z-index: 4;
	position: absolute;
	display: none;
	left: 0;
	min-width: 100%;
	height: auto;
	max-height: 300px;
	margin-bottom: 0;
	padding-left: 0;
	overflow: hidden;
	cursor: pointer;
	margin-top: 0;
	border-radius: 5px;
	div.wrapper {
		overflow-y: auto;
		overflow-x: hidden;
		max-height: 300px;
	}
	li {
		display: block;
		width: 100%;
		line-height: 1.33;
		position: relative;
		z-index: 1;
		border-top-color: transparent;
	}
	li:last-child {
		border-bottom-left-radius: 5px;
		border-bottom-right-radius: 5px;
	}
	li:active,
	li:hover,
	li:focus,
	li.hovering {
		outline: none;
	}
	li.selected {
		/* padding-right: calc(2.025rem + 0.8em); */
		position: relative;
		display: flex;
		align-items: center;
	}
}
.multiselect-wrapper.open ul.options {
	display: block;
}
input {
	width: 35%;
	z-index: 1;
	outline: none;
	cursor: pointer;
	line-height: 1.25;

	@media (max-width: 650px) {
		width: 100%;
	}

	&.empty {
		margin: 0;
		padding: 0;
	}
}
.bbox-multiselect {
	border-radius: 5px;
	/* padding: 0.8125rem 1.3125rem; */
	margin: 0;
	width: 100%;
	outline: none;
	position: relative;
	z-index: 2;
	cursor: pointer;
	line-height: 1.25;
}
span.input-error {
	display: block;
	text-align: left;
	color: #f95e5a;
	font-size: 1.67rem;
	margin-top: 0.8rem;
	line-height: 1.2;
}
.has-error .open span.input-error {
	display: none;
}
span.icon-left {
	position: absolute;
	top: 2rem;
	left: 2rem;
	z-index: 2;
	cursor: pointer;
}
span.icon-right {
	position: absolute;
	bottom: 1.8rem;
	right: 2rem;
	z-index: 2;
	cursor: pointer;
}
.chevron {
	background-position: center right 1rem;
	background-repeat: no-repeat;
	background-size: 2.5rem 2.5rem;
}
.chevron-up {
	background-image: url('https://s3-sa-east-1.amazonaws.com/app.bossabox.com/img/icon-chevron-up.svg');
}
.chevron-down {
	background-image: url('https://s3-sa-east-1.amazonaws.com/app.bossabox.com/img/icon-chevron-down.svg');
}
</style>

<docs>
    Caixa de seleção múltipla

    As importações dos arquivos abaixo são necessárias para o funcionamento do componente:
    - position.scss
    - spacing.scss
    - colors-classes.scss
    - typography.scss

    ```js

    let error = '';
    const selected = '';

    <div class="align-right" style="cursor: pointer"  @click="error === '' ? error = 'Mensagem de erro' : error = '';">Mostrar/ocultar mensagem de erro</div>


    <b-multi-select ref="multiselect" name="example" label="Label" v-model="selected" :options="['Opção 1', 'Opção 2', 'Opção 3', 'Opção 4']" :error="error" />

    ```
</docs>
